import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import { Banner } from "@rentiohq/web-shared/dist/components";
import { uniq } from "lodash";
import { ICostPaymentRecapRow } from "./costPaymentRecap.utils";

interface ICostPaymentRecapProps {
  rows: ICostPaymentRecapRow[];
}

const CENTS = 100;

const formatAmount = (amount: number) =>
  formatCurrency(Math.round(amount * CENTS) / CENTS);

// Who pays what, right above the summary of a cost payment: the steps that
// hold the payer, the amount and the description are not asked there, so
// the summary would not show them.
export const CostPaymentRecap = ({ rows }: ICostPaymentRecapProps) => {
  if (rows.length === 0) return null;

  const descriptions = uniq(
    rows.map(row => row.description).filter((d): d is string => !!d),
  );
  const isSharedDescription = descriptions.length === 1;
  const total = rows.reduce((sum, row) => sum + row.amountIncl, 0);

  const renderLine = (row: ICostPaymentRecapRow) => {
    const payer = [
      row.payerName ?? getLocalizedText("payment.payment_order.payer"),
      row.unitLabel,
    ]
      .filter(Boolean)
      .join(" · ");
    if (row.vatPct > 0) {
      return getLocalizedText("costs.payment_recap.line_vat", {
        payer,
        amount: formatAmount(row.amountExcl),
        vat: String(row.vatPct),
        total: formatAmount(row.amountIncl),
      });
    }
    return getLocalizedText("costs.payment_recap.line", {
      payer,
      total: formatAmount(row.amountIncl),
    });
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <Banner
        variation="info"
        hasDismiss={false}
        title={getLocalizedText("costs.payment_recap.title")}
      >
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {rows.map((row, index) => (
            <li key={index}>
              {renderLine(row)}
              {!isSharedDescription && row.description && (
                <div>
                  {getLocalizedText("costs.payment_recap.description", {
                    description: row.description,
                  })}
                </div>
              )}
            </li>
          ))}
        </ul>
        {isSharedDescription && (
          <div style={{ marginTop: 8 }}>
            {getLocalizedText("costs.payment_recap.description", {
              description: descriptions[0],
            })}
          </div>
        )}
        {rows.length > 1 && (
          <div style={{ marginTop: 8, fontWeight: 600 }}>
            {getLocalizedText("costs.payment_recap.total", {
              total: formatAmount(total),
            })}
          </div>
        )}
      </Banner>
    </div>
  );
};
